import getApplicationSubmissionStatus from 'api/application/getApplicationSubmissionStatus'
import {
    ApplicationSubmissionError,
    ApplicationSubmissionTimeoutError,
} from 'api/application/types.d'
import moment from 'moment'

type PollingResult = {
    status: string
    id: number
}
type OnCompletedCallback = (result: PollingResult) => void
type OnFailCallback = (error: Error) => void
type ApplicationSubmissionStatusPollerOptions = {
    applicationUuid: string
    timeoutSeconds: number
    pollIntervalSeconds: number
    maxPollIntervalSeconds: number
    pollIntervalMultiplier: number
    pollUrl?: string
}

export default class ApplicationSubmissionStatusPoller {
    private readonly applicationUuid: string

    private readonly timeoutSeconds: number

    private readonly pollIntervalSeconds: number

    private readonly maxPollIntervalSeconds: number

    private readonly pollIntervalMultiplier: number

    private readonly pollUrl?: string

    private currentInterval: number

    constructor({
        applicationUuid,
        timeoutSeconds,
        pollIntervalSeconds,
        maxPollIntervalSeconds,
        pollIntervalMultiplier,
        pollUrl,
    }: ApplicationSubmissionStatusPollerOptions) {
        this.applicationUuid = applicationUuid
        this.timeoutSeconds = timeoutSeconds
        this.pollIntervalSeconds = pollIntervalSeconds
        this.maxPollIntervalSeconds = maxPollIntervalSeconds
        this.pollIntervalMultiplier = pollIntervalMultiplier
        this.pollUrl = pollUrl
        this.currentInterval = pollIntervalSeconds
    }

    public async pollUntilCompletion(): Promise<PollingResult> {
        return new Promise((resolve, reject) => {
            setTimeout(
                () =>
                    this.pollApplicationStatus(
                        this.applicationUuid,
                        new Date(),
                        resolve,
                        reject,
                    ),
                this.pollIntervalSeconds * 1000,
            )
        })
    }

    private async pollApplicationStatus(
        applicationUuid: string,
        startTime: Date,
        onComplete: OnCompletedCallback,
        onFail: OnFailCallback,
    ) {
        try {
            const result = await getApplicationSubmissionStatus(
                applicationUuid,
                this.pollUrl,
            )

            if (!result.is_decision_pending) {
                onComplete({ status: result.status, id: result.id })

                return
            }

            if (result.is_submission_in_invalid_state) {
                onFail(new ApplicationSubmissionError())

                return
            }

            const elapsedTime = moment().diff(moment(startTime), 'seconds')

            if (elapsedTime >= this.timeoutSeconds) {
                onFail(new ApplicationSubmissionTimeoutError())

                return
            }

            this.currentInterval = Math.min(
                this.currentInterval * this.pollIntervalMultiplier,
                this.maxPollIntervalSeconds,
            )

            // schedule a new poll
            setTimeout(
                () =>
                    this.pollApplicationStatus(
                        applicationUuid,
                        startTime,
                        onComplete,
                        onFail,
                    ),
                this.currentInterval * 1000,
            )
        } catch (error) {
            onFail(error as Error)
        }
    }
}
